/**
 * Buyer address book service (shop.buyer_addresses).
 *
 * Per-buyer (NOT per-vendor), so the book is portable across vendors.
 * Distinct from shop.order_shipping_addresses, which is the historical
 * per-order snapshot.
 */

import { supabase } from '../client';

export interface ShopBuyerAddress {
  id: string;
  buyer_user_id: string;
  label: string | null;
  recipient_name: string;
  company: string | null;
  line1: string;
  line2: string | null;
  city: string;
  region: string | null;
  postal_code: string;
  country_iso2: string;
  phone: string | null;
  is_default: boolean;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  archived_by: string | null;
}

export async function listBuyerAddresses(): Promise<ShopBuyerAddress[]> {
  // RLS scopes to the caller. Use a tight default cap (100) since
  // address books are bounded by definition (typical buyer: <10).
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('buyer_addresses')
    .select('*')
    .is('archived_at', null)
    .order('is_default', { ascending: false })
    .order('last_used_at', { ascending: false, nullsFirst: false })
    .order('id', { ascending: false })
    .limit(100);
  if (error) throw error;
  return (data ?? []) as ShopBuyerAddress[];
}

export async function getBuyerAddress(id: string): Promise<ShopBuyerAddress | null> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('buyer_addresses')
    .select('*')
    .eq('id', id)
    .is('archived_at', null)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as ShopBuyerAddress | null;
}

export interface UpsertBuyerAddressInput {
  id?: string | null;
  label?: string | null;
  recipientName: string;
  company?: string | null;
  line1: string;
  line2?: string | null;
  city: string;
  region?: string | null;
  postalCode: string;
  countryIso2: string;
  phone?: string | null;
  isDefault?: boolean;
}

export async function upsertBuyerAddress(input: UpsertBuyerAddressInput): Promise<ShopBuyerAddress> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_buyer_address_upsert', {
      p_address_id:    input.id ?? null,
      p_label:         input.label ?? null,
      p_recipient_name: input.recipientName,
      p_company:       input.company ?? null,
      p_line1:         input.line1,
      p_line2:         input.line2 ?? null,
      p_city:          input.city,
      p_region:        input.region ?? null,
      p_postal_code:   input.postalCode,
      p_country_iso2:  input.countryIso2,
      p_phone:         input.phone ?? null,
      p_is_default:    input.isDefault ?? false,
    });
  if (error) throw error;
  return data as ShopBuyerAddress;
}

export async function archiveBuyerAddress(id: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_buyer_address_archive', { p_address_id: id });
  if (error) throw error;
}
